import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { OverlayBannerSubBlock } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

// CMS choice values (tsicommon/cms/blocks/sub_blocks/overlay_banner.py) to the
// module classes that style them.
const themeClasses: Readonly<Record<string, string | undefined>> = {
    "overlay-banner--promotional": styles.promotional,
    "overlay-banner--evergreen": styles.evergreen,
    "overlay-banner--limited-time": styles.limitedTime,
    "overlay-banner--transparent-text-block": styles.transparentTextBlock,
    "overlay-banner--diagonal": styles.diagonal,
};

const colorClasses: Readonly<Record<string, string | undefined>> = {
    "overlay-banner--chambray": styles.chambray,
    "overlay-banner--pelorous-blue": styles.pelorousBlue,
};

const alignmentClasses: Readonly<Record<string, string | undefined>> = {
    "middle-left": styles.middleLeft,
    "middle-right": styles.middleRight,
};

export const OverlayBannerBlock = (
    props: Immutable<{ value: OverlayBannerSubBlock }>,
): React.JSX.Element | null => {
    const v = props.value;
    if (!v.theme) {
        return null;
    }
    return (
        <section
            className={concatClassNames([
                styles.overlayBanner,
                "overlay-banner",
                themeClasses[v.theme],
                v.color ? colorClasses[v.color] : null,
                v.responsive_visibility?.visibility ?? "",
                v.content_alignment
                    ? alignmentClasses[v.content_alignment]
                    : null,
            ])}
        >
            {v.theme === "overlay-banner--promotional" && (
                <>
                    <RichTextBlock value={v.header ?? ""} />
                    <div aria-hidden="true" className={styles.divider}></div>
                    <div
                        className={concatClassNames([
                            styles.content,
                            "content",
                        ])}
                    >
                        {richT(
                            t`<row>SAVE <span>UP TO</span></row><row>${v.content}</row><row>on adjustable mattress sets<sup>¶</sup></row>`,
                            {
                                row: (children) => <div>{children}</div>,
                                span: (children) => <span>{children}</span>,
                            },
                        )}
                    </div>
                </>
            )}
            {v.theme === "overlay-banner--evergreen" && (
                <>
                    <div className="evergreen-title">
                        <h4>
                            {richT(
                                t`$300 INSTANT GIFT<br /><span>with your mattress set purchase</span>`,
                                {
                                    span: (children) => <span>{children}</span>,
                                },
                            )}
                        </h4>
                    </div>
                    {v.content && (
                        <>
                            <div
                                aria-hidden="true"
                                className={styles.divider}
                            ></div>
                            <div
                                className={concatClassNames([
                                    styles.content,
                                    "content",
                                ])}
                            >
                                {richT(
                                    t`<row>USE CODE</row><row>${v.content}</row>`,
                                    {
                                        row: (children) => (
                                            <div>{children}</div>
                                        ),
                                    },
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
            {v.theme === "overlay-banner--limited-time" && (
                <>
                    <RichTextBlock value={v.header ?? ""} />
                    <div aria-hidden="true" className={styles.divider}></div>
                    <div
                        className={concatClassNames([
                            styles.content,
                            "content",
                        ])}
                    >
                        {richT(t`LIMITED-TIME<br />OFFER`)}
                    </div>
                </>
            )}
            {(v.theme === "overlay-banner--transparent-text-block" ||
                v.theme === "overlay-banner--diagonal") && (
                <RichTextBlock value={v.header ?? ""} />
            )}
            {v.page && (
                <>
                    <div aria-hidden="true" className={styles.divider}></div>
                    <a className="call-to-action" href={v.page.url}>
                        {v.call_to_action}
                        <span
                            className={styles.triangle}
                            aria-hidden="true"
                        ></span>
                    </a>
                </>
            )}
        </section>
    );
};
